/**
 * 에피소드 길이 칸 선택을 서버 질의 축으로 바꾼다(순수 계산).
 *
 * facets 가 데이터셋마다 칸(`duration.bins`)을 준다. 경계는 **필터 없는 데이터셋** 범위에서 정하므로
 * 다른 필터가 바뀌어도 바뀌지 않는다. 그래서 화면은 칸 경계를 슬라이더 정지점으로 쓴다.
 * 칸 경계에만 멈추면 고른 칸 수의 합이 목록 total 과 정확히 같다. 서버는 칸 하나를
 * `duration_min=칸.min&duration_max=칸.max` 로 거른 목록과 같은 방식으로 센다.
 *
 * **칸은 반열림 `min <= x < max` 이고, 마지막 칸만 `max` 를 포함한다.** 그래서 다음 규칙을 쓴다:
 *   - 오른쪽 끝이 마지막 경계이면 `duration_max` 를 보내지 않는다. 보내면 가장 긴 에피소드가 빠진다.
 *   - 왼쪽 끝이 첫 경계이면 `duration_min` 을 보내지 않는다. 그보다 짧은 에피소드는 없다.
 *   - 양끝이 모두 끝이면 조건이 없다. 이때만 길이를 모르는 에피소드도 목록에 남는다.
 *     컷이 하나라도 있으면 길이를 모르는 에피소드는 빠진다.
 *
 * 선택은 경계 **인덱스** 구간 `[lo, hi]`(0 ≤ lo < hi ≤ 칸 수)로 다룬다. 슬라이더는 균일한 step 만
 * 지원하므로, 초 단위로는 칸 폭이 다른 경계에 멈출 수 없다.
 */

import type { EpisodeListFilters, EpisodeRangeBin } from "@/api/datasetEpisode";

/** 경계 인덱스 구간 `[lo, hi]`. 칸 `lo` 부터 칸 `hi - 1` 까지다. */
export type DurationIndexRange = readonly [number, number];

/** 칸을 경계 배열(칸 수 + 1)로 바꾼다. 칸이 없으면 빈 배열이다. */
export function durationEdges(bins: readonly EpisodeRangeBin[]): readonly number[] {
  if (bins.length === 0) {
    return [];
  }
  return [bins[0].min, ...bins.map((bin) => bin.max)];
}

/** 길이 컷이 하나라도 적용되어 있는지 반환한다. */
export function hasDurationCut(filters: EpisodeListFilters): boolean {
  return filters.duration_min !== undefined || filters.duration_max !== undefined;
}

/**
 * 현재 적용된 컷을 경계 인덱스로 다시 읽는다. 컷이 없으면 전체 `[0, 칸 수]` 다.
 * 경계와 맞지 않는 수치(MCP·다른 세션이 적용한 임의 값)이면 null 이다. 슬라이더가 없는 정지점을
 * 만들지 않기 위해서다. 그 컷은 칩이 수치 그대로 표시한다.
 */
export function readDurationRange(
  filters: EpisodeListFilters,
  edges: readonly number[],
): DurationIndexRange | null {
  const last = edges.length - 1;
  if (last < 1) {
    return null;
  }
  const lo = filters.duration_min === undefined ? 0 : edges.indexOf(filters.duration_min);
  // 마지막 경계를 max 로 보내면 가장 긴 에피소드가 빠진다. 뜻이 다르므로 정지점으로 보지 않는다.
  const hi = filters.duration_max === undefined ? last : edges.indexOf(filters.duration_max);
  if (lo < 0 || hi < 0 || lo >= hi || (filters.duration_max !== undefined && hi === last)) {
    return null;
  }
  return [lo, hi];
}

/** 경계 인덱스 구간을 반영한 사본. 양끝이 모두 끝이면 길이 조건을 제거한다. */
export function applyDurationRange(
  filters: EpisodeListFilters,
  edges: readonly number[],
  [lo, hi]: DurationIndexRange,
): EpisodeListFilters {
  const rest = withoutDuration(filters);
  const last = edges.length - 1;
  return {
    ...rest,
    ...(lo > 0 ? { duration_min: edges[lo] } : {}),
    ...(hi < last ? { duration_max: edges[hi] } : {}),
  };
}

/** 길이 조건만 제거한 사본. */
export function withoutDuration(filters: EpisodeListFilters): EpisodeListFilters {
  const rest = { ...filters };
  delete rest.duration_min;
  delete rest.duration_max;
  return rest;
}

/** 고른 칸 수의 합. 다른 조건을 적용한 수이므로, 그 구간을 적용했을 때의 목록 total 과 같다. */
export function durationRangeCount(
  bins: readonly EpisodeRangeBin[],
  [lo, hi]: DurationIndexRange,
): number {
  return bins.slice(lo, hi).reduce((sum, bin) => sum + bin.count, 0);
}

/** 칩 문구. 수치만 적는다. 칸 수는 드롭다운이 표시한다. */
export function durationChipText(filters: EpisodeListFilters): string {
  const min = filters.duration_min;
  const max = filters.duration_max;
  if (min === 0 && max !== undefined) {
    return `${formatSeconds(max)}초 미만`;
  }
  if (min !== undefined && max !== undefined) {
    return `${formatSeconds(min)}–${formatSeconds(max)}초`;
  }
  if (min !== undefined) {
    return `${formatSeconds(min)}초 이상`;
  }
  return `${formatSeconds(max ?? 0)}초 미만`;
}

/** 초 표기. 소수 한 자리까지 쓰고 끝의 `.0` 은 뺀다. 예: `8` · `12.5` */
export function formatSeconds(seconds: number): string {
  return String(Math.round(seconds * 10) / 10);
}
